/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { CSSProperties, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Period, PublishedShiftSummary, ShiftsNav } from 'types/ShiftTypes';
import Button from 'components/Button';
import { useFeatureFlag } from 'hooks/feature';
import colors from 'config/colors';

import { isFeatureOn } from 'lib/features';
import { featureFlags } from 'config/featureFlags';
import { permissions } from 'config/permissions';

import { useSelectedShifts } from '../shift-hooks';
import { CreateShiftButton } from './ShiftsHeaderButtons';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ConfirmApproveModal from './ConfirmApproveModal';
import BulkAgencyRelease from './BulkAgencyRelease';
import SaveToTemplateModal from './SaveToTemplateModal';

const styles = {
  shiftTotalText: {
    color: '#999',
    padding: '3px 12px',
    borderBottom: '1px solid rgba(164, 194, 244, 0.2)',
    fontSize: '0.9rem',
  },
  bottomBar: {
    backgroundColor: colors.dagny.backgroundLight6,
    padding: '12px 12px',
    // borderTop: '1px solid rgba(164, 194, 244, 0.2)',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '35px',
  },
  messageBar: {
    color: colors.dagny.error,
    padding: '5px 25px',
    borderTop: '1px solid rgba(164, 194, 244, 0.2)',
    alignItems: 'center',
    display: 'flex',
    fontSize: '11px',
    justifyContent: 'flex-end',
    paddingRight: '25px',
  },
  bottomBarText: {
    textAlign: 'left' as CSSProperties['textAlign'],
    fontSize: '0.9rem',
    lineHeight: 1.2,
    color: colors.text,
  },
};

interface BulkActionBarProps {
  period: Period,
  nav: ShiftsNav,
  shiftList: Array<PublishedShiftSummary>;
  pendingApprovalShiftList: Array<PublishedShiftSummary>,
  refreshShiftList: () => void,
}

function getButtonLabel({ shiftsNeedApprovalBeforePublish, userHasPublishPermission }: { [key: string]: boolean }): string {
  if (shiftsNeedApprovalBeforePublish && !userHasPublishPermission) return 'Review & Submit';
  return 'Review & Publish';
}

const BulkActionBar = React.memo((props: BulkActionBarProps) : JSX.Element | null => {

  const [confirmApproveModalIsOpen, setConfirmApproveModalIsOpen] = useState(false);
  const [confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen] = useState(false);
  const [saveToTemplateModalIsOpen, setSaveToTemplateModalIsOpen] = useState(false);
  const [agencyReleaseModalIsOpen, setAgencyReleaseModalIsOpen] = useState(false);

  const isMobile = useSelector(state => state.screen.isMobile);

  const currentUserAdminGroup = useSelector(state => state.user.adminGroups);

  const allDraftShifts = Object.values(useSelector(state => state.createShifts.draftShifts) ?? {});
  const { selectedShiftKeys, unselectSpecifiedShifts, unselectAllShifts } = useSelectedShifts(props.period);

  const shiftList = props.shiftList;
  const pendingApprovalShiftList = props.pendingApprovalShiftList || {};

  const draftShifts = useMemo(() => shiftList.filter(shift => shift.isDraft), [shiftList]);
  const nonDraftShifts = useMemo(() => shiftList.filter(shift => !shift.isDraft), [shiftList]);
  const waitingForApprovalShifts = useMemo(() => Object.values(pendingApprovalShiftList).filter(shift => (!shift.isDraft)), [pendingApprovalShiftList]);

  const selectedDraftShifts = useMemo(() => draftShifts.filter(shift => selectedShiftKeys.includes(shift.key)), [draftShifts, selectedShiftKeys]);
  const selectedNonDraftShifts = useMemo(() => nonDraftShifts.filter(shift => selectedShiftKeys.includes(shift.key)), [nonDraftShifts, selectedShiftKeys]);
  const selectedPendingApprovalShifts = useMemo(() => nonDraftShifts.filter(shift => selectedShiftKeys.includes(shift.key) && !shift.approvedAt && !shift.approvedBy), [nonDraftShifts, selectedShiftKeys]);
  // const selectedApprovedShifts = useMemo(() => nonDraftShifts.filter(shift => selectedShiftKeys.includes(shift.key) && shift.approvedAt && shift.approvedBy), [nonDraftShifts, selectedShiftKeys]);

  const adminCanDeleteShifts = useFeatureFlag(null, 'jobsCanDeleteJobs');
  const adminCanCreateShifts = useFeatureFlag(null, 'jobsCanCreate');

  const shiftsNeedApprovalBeforePublish = useFeatureFlag('shiftsNeedApprovalBeforePublish');
  const userHasPublishPermission = useFeatureFlag(null, permissions.JOBS_CAN_PUBLISH);

  // Check that agency feature flag is on, and admin has release to agency permission
  const adminCanReleaseToAgency = useFeatureFlag('vms', 'vmsCanApproveAgencyRequests');
  const canSaveToTemplateFromCurrentView = ['day', 'week', 'week-am-pm'].includes(props.period.duration);

  const invalidDraftsSelected = selectedDraftShifts.some(shift => !shift.isValid);

  if (!selectedShiftKeys.length &&
      ((allDraftShifts.length > 0) ||
      ((shiftsNeedApprovalBeforePublish && userHasPublishPermission) && waitingForApprovalShifts.length > 0))) {
    return (
      <div style={{ ...styles.bottomBar }}>
        <div style={{ ...styles.bottomBarText }}>
          You have <strong>{allDraftShifts.length}</strong> unpublished {isMobile ? 'draft' : 'draft shift'}{allDraftShifts.length === 1 ? '' : 's'}
          {
            (shiftsNeedApprovalBeforePublish && userHasPublishPermission) && (
              <>
                {' and '}
                <strong>{waitingForApprovalShifts.length}</strong>
                {' pending-approval'}
                {waitingForApprovalShifts.length === 1 ? ' shift' : ' shifts'}
              </>
            )
          }
        </div>
        <div style={{ width: 12, height: 12, flex: '0 0 auto' }} />
        <div style={{ flex: '0 0 auto' }}>
          <Button
            black
            onClick={() => props.nav.goToConfirmDraftsView()}
          >{getButtonLabel({ shiftsNeedApprovalBeforePublish, userHasPublishPermission })}
          </Button>
        </div>
      </div>
    );
  }

  if (!selectedShiftKeys.length) return null;
  // console.log(selectedShiftKeys);

  const allSelectedShiftsAreDraftShifts = selectedShiftKeys.length === selectedDraftShifts.length;
  const allSelectedShiftsArePendingShifts = selectedShiftKeys.length === selectedPendingApprovalShifts.length;
  let approveButtonEnabled = false;
  if (selectedShiftKeys.length) {
    // if adminGroup doesn't match
    // console.log(selectedNonDraftShifts);
    // console.log(selectedApprovedShifts);
    // currentUserAdminGroup
    const adminGroupKeys = selectedPendingApprovalShifts.map(e => e.adminGroups?.map(ag => ag.key));
    // console.log(currentUserAdminGroup);
    // console.log(adminGroupKeys);
    approveButtonEnabled = adminGroupKeys.every(subArray => subArray && subArray.some(item => currentUserAdminGroup.includes(item)));
  }

  return (
    <>
      { !approveButtonEnabled && (
        <div style={{ ...styles.messageBar }}>
          You are not in an Admin Group which has permissions to approve & publish this/these shifts
        </div>)
    }
      <div style={{ ...styles.bottomBar, justifyContent: 'space-between' }}>

        {/* Selected shifts counts */}
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'baseline', borderRight: '1px solid #EAEAEA' }}>
          <div style={{ color: colors.text, fontSize: '0.9rem', marginRight: 12 }}>
            Selected:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', marginRight: 12 }}>
            {!!selectedNonDraftShifts.length && (
            <div style={{ color: colors.text, lineHeight: 1 }}>
              {selectedNonDraftShifts.length} / {nonDraftShifts.length} valid shifts
            </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', marginRight: 12 }}>
            {!!selectedDraftShifts.length && (
            <div style={{ color: colors.text, lineHeight: 1 }}>
              {selectedDraftShifts.length} / {draftShifts.length} drafts
            </div>
            )}
          </div>
          <a
            className="link"
            style={{ fontSize: '0.9rem', marginRight: 12 }}
            onClick={() => unselectAllShifts()}
          >
            Deselect all
          </a>
        </div>

        {/* Action buttons */}
        <div className="space-children-12" style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', padding: '0 12px' }}>
          <div style={{ color: colors.text, fontSize: '0.9rem', marginRight: 12 }}>
            Actions:
          </div>
          {adminCanCreateShifts && canSaveToTemplateFromCurrentView && (
          <Button
            outline
            white
            disabled={invalidDraftsSelected}
            onClick={() => setSaveToTemplateModalIsOpen(true)}
          >
            Save To Template
          </Button>
          )}

          {(shiftsNeedApprovalBeforePublish && userHasPublishPermission && allSelectedShiftsArePendingShifts) && (
          <Button
            onClick={() => setConfirmApproveModalIsOpen(true)}
            disabled={!approveButtonEnabled}
          >
            Approve Shifts
          </Button>
          )}

          {/* {(shiftsNeedApprovalBeforePublish && userHasPublishPermission && allSelectedShiftsAreDraftShifts) && (
        <Button
          onClick={() => setConfirmApproveModalIsOpen(true)}
          disabled={false}
        >
          Publish Shifts
        </Button>
        )} */}

          {adminCanReleaseToAgency && (
          <Button
            onClick={() => setAgencyReleaseModalIsOpen(true)}
          >
            Release To Agency
          </Button>
          )}

          {adminCanDeleteShifts && (
          <Button
            danger
            style={{ marginRight: 12 }}
            onClick={() => {
              setConfirmDeleteModalIsOpen(true);
            }}
          >
            Delete
          </Button>
          )}

          {/*        <Button positive style={{ marginRight: 12 }} onClick={() => window.alert('Not implemented!')}>
          Copy
        </Button>
        <Button positive style={{ marginRight: 12 }} onClick={() => window.alert('Not implemented!')}>
          Message Staff
        </Button> */}
        </div>

        {saveToTemplateModalIsOpen && (
        <SaveToTemplateModal
          selectedShifts={[...selectedDraftShifts, ...selectedNonDraftShifts]}
          onClose={() => setSaveToTemplateModalIsOpen(false)}
          unselectSpecifiedShifts={unselectSpecifiedShifts}
          replaceToTemplateEditView={props.nav.replaceToTemplateEditView}
        />
        )}

        {confirmApproveModalIsOpen && (
        <ConfirmApproveModal
          selectedDraftShifts={selectedDraftShifts}
          selectedNonDraftShifts={selectedNonDraftShifts}
          onClose={() => setConfirmApproveModalIsOpen(false)}
          unselectSpecifiedShifts={unselectSpecifiedShifts}
          refreshShiftList={props.refreshShiftList}
        />
        )}

        {confirmDeleteModalIsOpen && (
        <ConfirmDeleteModal
          selectedDraftShifts={selectedDraftShifts}
          selectedNonDraftShifts={selectedNonDraftShifts}
          onClose={() => setConfirmDeleteModalIsOpen(false)}
          unselectSpecifiedShifts={unselectSpecifiedShifts}
          refreshShiftList={props.refreshShiftList}
        />
        )}

        {agencyReleaseModalIsOpen && (
        <BulkAgencyRelease
          refreshShiftList={props.refreshShiftList}
          unselectSpecifiedShifts={unselectSpecifiedShifts}
          shifts={selectedNonDraftShifts}
          selectedDrafts={selectedDraftShifts}
          onClose={() => setAgencyReleaseModalIsOpen(false)}
        />
        )}
      </div>
    </>
  );
});

export default BulkActionBar;

